import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Search, Filter, ChevronLeft, RefreshCw, Truck } from 'lucide-react';

const n   = v => Number(v) || 0;
const fmt = d => { try { return d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'; } catch { return '—'; } };
const todayStr = () => new Date().toISOString().split('T')[0];

export default function DashboardOutsourcePage() {
  const navigate = useNavigate();
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [urgency, setUrgency]   = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/reports/dashboard/outsource-overview')
      .then(r => setData(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = data.filter(job => {
    const q = search.toLowerCase();
    const matchSearch = !q || job.vendor_name?.toLowerCase().includes(q) || job.project_name?.toLowerCase().includes(q) || job.proto_code?.toLowerCase().includes(q) || job.item_name?.toLowerCase().includes(q);
    const matchUrgency = urgency==='all' || job.urgency===urgency;
    return matchSearch && matchUrgency;
  });

  const counts = { overdue: data.filter(j=>j.urgency==='overdue').length, next_24h: data.filter(j=>j.urgency==='next_24h').length, on_time: data.filter(j=>j.urgency==='on_time').length };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={()=>navigate('/admin')} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-500"/>
        </button>
        <div>
          <h1 className="text-xl font-black text-gray-900">Outsource Overview</h1>
          <p className="text-xs text-gray-400">{filtered.length} jobs pending</p>
        </div>
        <button onClick={load} className="ml-auto p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <RefreshCw className={`w-4 h-4 text-gray-400 ${loading?'animate-spin':''}`}/>
        </button>
      </div>

      {/* Summary badges */}
      <div className="grid grid-cols-3 gap-3">
        {[
          {key:'overdue',  label:'Overdue',   color:'bg-red-50 border-red-200',    text:'text-red-600',    count:counts.overdue},
          {key:'next_24h', label:'Next 24h',  color:'bg-orange-50 border-orange-200', text:'text-orange-600', count:counts.next_24h},
          {key:'on_time',  label:'On Time',   color:'bg-green-50 border-green-200', text:'text-green-600', count:counts.on_time},
        ].map(s=>(
          <button key={s.key} onClick={()=>setUrgency(urgency===s.key?'all':s.key)}
            className={`rounded-2xl border p-4 text-left transition-all ${s.color} ${urgency===s.key?'ring-2 ring-offset-1 ring-current':''}`}>
            <p className={`text-2xl font-black ${s.text}`}>{s.count}</p>
            <p className={`text-xs font-semibold ${s.text}`}>{s.label}</p>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] border border-gray-200 rounded-xl px-3 py-2">
          <Search className="w-4 h-4 text-gray-300 flex-shrink-0"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search vendor, project, proto code..."
            className="flex-1 text-sm outline-none text-gray-700 placeholder-gray-300"/>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-300"/>
          <select value={urgency} onChange={e=>setUrgency(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-600 outline-none">
            <option value="all">All Status</option>
            <option value="overdue">Overdue</option>
            <option value="next_24h">Next 24h</option>
            <option value="on_time">On Time</option>
          </select>
        </div>
        <p className="text-xs text-gray-400 ml-auto">{filtered.length} of {data.length} jobs</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"/>
          </div>
        ) : filtered.length===0 ? (
          <div className="text-center py-16">
            <Truck className="w-12 h-12 text-gray-200 mx-auto mb-3"/>
            <p className="text-gray-300 text-sm">No outsource jobs found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  {['S.No','Outsource Name','Order Placed Date','Due Date','Proto Code','Project Name','Days Until Due','Qty Sent','Status'].map(h=>(
                    <th key={h} className="text-left text-[11px] font-semibold text-gray-400 uppercase px-4 py-3 whitespace-nowrap tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((job,idx)=>{
                  const isOverdue = job.urgency==='overdue' || (job.expected_date&&job.expected_date.split('T')[0]<todayStr());
                  const isNext24h = job.urgency==='next_24h';
                  const statusLbl = isOverdue?'Overdue':isNext24h?'Next 24h':'On Time';
                  const statusCls = isOverdue?'bg-red-100 text-red-600':isNext24h?'bg-orange-100 text-orange-600':'bg-green-100 text-green-700';
                  const daysUntil = job.days_until_due!==null&&job.days_until_due!==undefined ? Number(job.days_until_due) : null;
                  const daysColor = isOverdue?'text-red-600 font-bold':isNext24h?'text-orange-500 font-bold':'text-gray-700';
                  const daysLabel = daysUntil===null?'—':isOverdue?`${Math.abs(daysUntil)}d ago`:`${daysUntil}d`;
                  return (
                    <tr key={job.id} className="hover:bg-amber-50/20 cursor-pointer transition-colors"
                      onClick={()=>navigate('/admin/outsource')}>
                      <td className="px-4 py-3 text-xs text-gray-400 font-semibold">{idx+1}</td>
                      <td className="px-4 py-3 font-semibold text-gray-800 text-sm whitespace-nowrap">{job.vendor_name}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmt(job.order_placed_date)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-medium whitespace-nowrap">{fmt(job.expected_date)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600 whitespace-nowrap">{job.proto_code||'—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{job.project_name||'—'}</td>
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${daysColor}`}>{daysLabel}</td>
                      <td className="px-4 py-3 text-sm font-bold text-gray-700">{n(job.qty_sent)||'—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex text-xs font-semibold px-2.5 py-0.5 rounded-full ${statusCls}`}>{statusLbl}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
